<template>
    <kiosk-layout>
        <div class="flex flex-col gap-12 lg:flex-row">
            <div class="shrink-0 lg:w-60">
                <kiosk-migrations-sidebar v-model="fetchParams"></kiosk-migrations-sidebar>
            </div>
            <div class="w-full">
                <kiosk-migrations-controls @reset="resetFilters"></kiosk-migrations-controls>
                <page-loading v-if="loading && !list.length"></page-loading>

                <vx-box
                    v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    <div class="w-64 mx-auto text-center">
                        No results for current search, try adjusting your filters.
                    </div>
                    <vx-button type="button" color="secondary" @click="resetFilters">
                        Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                    </vx-button>
                </vx-box>

                <vx-box
                    v-else-if="!list.length"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    No migrations found.
                </vx-box>

                <div v-if="list.length">
                    <div class="text-xl font-extrabold">Migrations</div>
                    <kiosk-migrations-table>
                        <kiosk-migration-row
                            v-for="(row, index) in list"
                            v-model="list[index]"
                            :key="row.id"
                            @deleted="handleDeleted"
                        ></kiosk-migration-row>
                    </kiosk-migrations-table>

                    <div v-if="fetchParams.cursor" class="mt-8">
                        <vx-button
                            @click="loadData()"
                            color="muted-dark"
                            size="xl"
                            class="grow-0"
                            data-test="load-more-btn"
                            :loading="loading"
                        >
                            Load More
                            <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import { faCircleX, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskMigrationRow from '~/components/kiosk/kiosk-migrations/list/KioskMigrationRow.vue';
import KioskMigrationsControls from '~/components/kiosk/kiosk-migrations/list/KioskMigrationsControls.vue';
import KioskMigrationsSidebar from '~/components/kiosk/kiosk-migrations/list/KioskMigrationsSidebar.vue';
import KioskMigrationsTable from '~/components/kiosk/kiosk-migrations/list/KioskMigrationsTable.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { adminTeamMigrationsClient } from '~/services';
import { isEqual } from '~/utils/helpers';

const defaultFilters = () => ({
    status: undefined,
    target_team_id: undefined,
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 10,
    cursor: null,
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination();
fetchParams.value = defaultFilters();

watch([() => fetchParams.value.status, () => fetchParams.value.target_team_id], () => {
    fetchParams.value.cursor = null;
    loadData();
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const loadData = () => {
    fetchRecords(adminTeamMigrationsClient.listMigrations(fetchParams.value));
};

const handleDeleted = (migrationId) => {
    const index = list.value.findIndex((m) => m.id === migrationId);
    if (index !== -1) {
        list.value.splice(index, 1);
    }
};

resetFilters();
</script>
